<template>
  <div class="download-page">
    <div class="download-container">
      <!-- Logo -->
      <div class="logo-section">
        <img 
          v-if="currentLogo" 
          :src="currentLogo" 
          :alt="settings?.siteName || 'Logo'" 
          class="logo"
          @error="handleImageError"
        />
      </div>

      <!-- Informações da versão -->
      <div v-if="latestVersion" class="version-card">
        <div class="version-header">
          <i class="fas fa-mobile-alt"></i>
          <h2>Baixe o Aplicativo</h2>
        </div>
        
        <div class="version-info">
          <div class="info-item">
            <span class="label">Versão:</span>
            <span class="value">{{ latestVersion.versionName }}</span>
          </div>
          <div class="info-item">
            <span class="label">Data de Lançamento:</span>
            <span class="value">{{ formatDate(latestVersion.releaseDate) }}</span>
          </div>
          <div class="info-item">
            <span class="label">Tamanho:</span>
            <span class="value">{{ formatFileSize(latestVersion.fileSize) }}</span>
          </div>
        </div>

        <!-- Notas de lançamento -->
        <div v-if="latestVersion.releaseNotes" class="release-notes">
          <h3>Novidades nesta versão:</h3>
          <p class="notes-text">{{ latestVersion.releaseNotes }}</p>
        </div>

        <!-- Botão de download -->
        <button @click="downloadApp" class="download-button" :disabled="downloading">
          <i class="fas fa-download"></i>
          <span v-if="!downloading">Baixar Aplicativo</span>
          <span v-else>Baixando...</span>
        </button>

        <!-- Instruções de instalação -->
        <div class="instructions">
          <h3>Como instalar:</h3>
          <ol>
            <li>Clique no botão "Baixar Aplicativo" acima</li>
            <li>Aguarde o download ser concluído</li>
            <li>Abra o arquivo APK baixado</li>
            <li>Se solicitado, permita a instalação de apps de fontes desconhecidas</li>
            <li>Siga as instruções na tela para instalar</li>
          </ol>
        </div>
      </div>

      <!-- Loading -->
      <div v-else-if="loading" class="loading">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Carregando informações...</p>
      </div>

      <!-- Erro -->
      <div v-else class="error-message">
        <i class="fas fa-exclamation-circle"></i>
        <p>Não foi possível carregar as informações da versão.</p>
        <button @click="loadLatestVersion" class="retry-button">
          <i class="fas fa-redo"></i>
          Tentar novamente
        </button>
      </div>

      <!-- Footer -->
      <div class="footer">
        <p>&copy; 2026 Ralph. Todos os direitos reservados.</p>
        <p class="support">Precisa de ajuda? Entre em contato com o suporte.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { http } from '@/services/config';
import mainService from '@/services/mainService';

export default {
  name: 'ViewDownload',
  data() {
    return {
      latestVersion: null,
      loading: true,
      downloading: false,
      settings: null,
      currentLogo: null,
      isDarkTheme: false,
    };
  },
  async mounted() {
    await this.loadSettings();
    this.checkCurrentTheme();
    this.setCurrentLogo();
    this.loadLatestVersion();
    
    // Escutar mudanças de tema
    this.themeObserver = new MutationObserver(this.handleThemeChange);
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
  },
  beforeUnmount() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },
  methods: {
    async loadLatestVersion() {
      this.loading = true;
      try {
        const response = await http.get('/api/v1/app/version/latest');
        // A API retorna os dados diretamente
        this.latestVersion = response.data;
      } catch (error) {
        this.latestVersion = null;
      } finally {
        this.loading = false;
      }
    },
    async downloadApp() {
      if (!this.latestVersion || this.downloading) return;

      this.downloading = true;
      
      try {
        const downloadUrl = this.latestVersion.downloadUrl;
        
        // Criar um link temporário para forçar o download
        const link = document.createElement('a');
        link.href = downloadUrl;
        
        // Extrair o nome do arquivo da URL ou usar um padrão
        const fileName = downloadUrl.split('/').pop() || `ABApp_v${this.latestVersion.versionName}.apk`;
        link.download = fileName;
        
        // Adicionar ao DOM temporariamente
        document.body.appendChild(link);
        
        // Clicar no link programaticamente
        link.click();
        
        // Remover o link do DOM
        document.body.removeChild(link);
        
        // Aguardar um pouco antes de habilitar o botão novamente
        setTimeout(() => {
          this.downloading = false;
        }, 2000);
      } catch (error) {
        alert('Erro ao iniciar o download. Por favor, tente novamente.');
        this.downloading = false;
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    formatFileSize(bytes) {
      const mb = bytes / (1024 * 1024);
      return `${mb.toFixed(2)} MB`;
    },
    async loadSettings() {
      try {
        const response = await mainService.getActiveSettings();
        if (response.data) {
          this.settings = response.data;
          this.setCurrentLogo();
        }
      } catch (error) {
        this.settings = null;
      }
    },
    checkCurrentTheme() {
      this.isDarkTheme = document.documentElement.hasAttribute('data-theme') && 
                        document.documentElement.getAttribute('data-theme') === 'dark';
    },
    setCurrentLogo() {
      if (!this.settings) return;
      
      this.checkCurrentTheme();
      
      if (this.isDarkTheme && this.settings.logoDark) {
        this.currentLogo = this.settings.logoDark;
      } else if (!this.isDarkTheme && this.settings.logoLight) {
        this.currentLogo = this.settings.logoLight;
      } else {
        this.currentLogo = this.settings.logoLight || this.settings.logoDark || null;
      }
    },
    handleThemeChange() {
      this.setCurrentLogo();
    },
    handleImageError(event) {
      if (this.isDarkTheme && this.settings?.logoLight) {
        event.target.src = this.settings.logoLight;
      } else if (!this.isDarkTheme && this.settings?.logoDark) {
        event.target.src = this.settings.logoDark;
      }
    }
  },
};
</script>

<style scoped>
.download-page {
  min-height: 100vh;
  background: var(--bg-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}

.download-container {
  max-width: 600px;
  width: 100%;
}

.logo-section {
  text-align: center;
  margin-bottom: 30px;
  padding: 20px;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--card-border);
}

.logo {
  max-width: 300px;
  max-height: 100px;
  width: auto;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
}

.logo-section h1 {
  color: var(--text-primary);
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
}

.version-card {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 40px;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--card-border);
}

.version-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--border-color);
}

.version-header i {
  font-size: 2rem;
  color: var(--accent-color);
}

.version-header h2 {
  margin: 0;
  font-size: 1.8rem;
  color: var(--text-primary);
}

.version-info {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 25px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.info-item .label {
  font-weight: 600;
  color: var(--text-secondary);
}

.info-item .value {
  font-weight: 700;
  color: var(--text-primary);
  font-size: 1.1rem;
}

.release-notes {
  margin-bottom: 25px;
  padding: 20px;
  background: var(--bg-secondary);
  border-radius: 12px;
  border-left: 4px solid var(--accent-color);
  border: 1px solid var(--border-color);
}

.release-notes h3 {
  margin: 0 0 15px 0;
  color: var(--text-primary);
  font-size: 1.1rem;
}

.notes-text {
  margin: 0;
  color: var(--text-secondary);
  line-height: 1.6;
  white-space: pre-wrap;
}

.download-button {
  width: 100%;
  padding: 18px;
  background: var(--success);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.2rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
  margin-bottom: 30px;
  box-shadow: 0 10px 25px rgba(16, 185, 129, 0.2);
}

.download-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(16, 185, 129, 0.4);
  background: #2bdda2;
}

.download-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.download-button i {
  font-size: 1.3rem;
}

.instructions {
  margin-bottom: 0;
}

.instructions h3 {
  margin: 0 0 15px 0;
  color: var(--text-primary);
  font-size: 1.1rem;
}

.instructions ol {
  margin: 0;
  padding-left: 20px;
  color: var(--text-secondary);
  line-height: 1.8;
}

.instructions li {
  margin-bottom: 8px;
  text-align: left;
}

.loading, .error-message {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 60px 40px;
  text-align: center;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--card-border);
}

.loading i {
  font-size: 3rem;
  color: var(--accent-color);
  margin-bottom: 20px;
}

.loading p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 1.1rem;
}

.error-message i {
  font-size: 3rem;
  color: var(--danger-color);
  margin-bottom: 20px;
}

.error-message p {
  margin: 0 0 20px 0;
  color: var(--text-secondary);
  font-size: 1.1rem;
}

.retry-button {
  padding: 12px 30px;
  background: var(--accent-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;
}

.retry-button:hover {
  background: var(--accent-hover);
}

.footer {
  text-align: center;
  margin-top: 30px;
  padding: 20px;
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid var(--card-border);
}

.footer p {
  margin: 5px 0;
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.footer .support {
  font-size: 0.85rem;
  color: var(--text-muted);
}

@media (max-width: 640px) {
  .logo {
    max-width: 200px;
    max-height: 60px;
  }

  .logo-section h1 {
    font-size: 2rem;
  }

  .version-card {
    padding: 25px;
  }

  .version-header {
    flex-direction: column;
    text-align: center;
  }

  .version-header h2 {
    font-size: 1.5rem;
  }
}
</style>

